import { useState } from "react";
import { toast } from "sonner";

interface IFetchOptions<P, R> {
  showsToast?: boolean;
  showsErrorToast?: boolean;
  successMsg?: string;
  failureMsg?: string;
  onSuccess?: (res: R) => void;
  onValidate?: (params: P) => string;
  onFail?: (error: unknown) => void;
}

interface IFetch<P, R> {
  fetchFunc: (params: P) => Promise<R>;
  options: IFetchOptions<P, R>;
}

export const useNotification = () => {
  const showNotification = (description: string) => {
    toast(description);
  };

  return showNotification;
};

export const useFetch = <P = unknown, R = unknown>({
  fetchFunc,
  options,
}: IFetch<P, R>) => {
  const {
    showsToast = false,
    showsErrorToast = true,
    successMsg = "",
    failureMsg = "",
    onValidate = () => "",
    onSuccess,
    onFail,
  } = options;

  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(failureMsg);
  const showNotification = useNotification();

  const handleFetch = async (params: P) => {
    const validationError = onValidate(params);
    if (validationError) {
      setErrorMsg(validationError);
      return;
    }
    setIsLoading(true);

    try {
      const response = await fetchFunc(params);
      if (onSuccess) {
        onSuccess(response);
      }
      if (showsToast) {
        showNotification(successMsg);
      }
      return response;
    } catch (error: unknown) {
      if (onFail) {
        onFail(error);
      }

      const errorMessage =
        (error as { data?: { error?: { message?: string } } })?.data?.error?.message || failureMsg || "Đã xảy ra lỗi";
      setErrorMsg(errorMessage);

      if (showsErrorToast) {
        showNotification(errorMessage);
      }
      return error;
    } finally {
      setIsLoading(false);
    }
  };

  return { isLoading, errorMsg, handleFetch };
};
